import { resilientFetch } from '@infra/network/resilientFetch';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { resolveLicensingApiBase } from '@infra/licence/activationConstants';
import { logger } from '@infra/telemetry/Logger';
import { isBrowserCloudHost } from '@application/licence/CloudWebHost';

interface BusinessSessionResponse {
  session_token: string;
  expires_at: string;
  tenant_id: string;
}

/** Échange certificat d’activation → jeton d’installation (licensing-api). */
export class LicensingSessionService {
  static async ensureInstallationSession(expectedTenantId?: string): Promise<boolean> {
    // Navigateur Cloud : la session est un cookie httpOnly posé par business-api, sans certificat ni jeton d'installation.
    if (isBrowserCloudHost()) return true;
    const existing = await SecureLicenceStore.getInstallationSessionToken();
    const expires = await SecureLicenceStore.getInstallationSessionExpiresAt();
    const storedTenant = await SecureLicenceStore.getInstallationSessionTenantId();
    if (existing && expires && new Date(expires) > new Date()) {
      if (expectedTenantId && storedTenant && storedTenant !== expectedTenantId) {
        logger.warn({
          event: 'installation_session_tenant_mismatch',
          storedTenant,
          expectedTenant: expectedTenantId,
        });
        await SecureLicenceStore.clearInstallationSession();
      } else {
        return true;
      }
    }

    const base = resolveLicensingApiBase();
    const certificate = await SecureLicenceStore.getActivationCertificate();
    const fingerprint = await SecureLicenceStore.getDeviceFingerprint();
    if (!certificate || !fingerprint) {
      logger.warn({ event: 'installation_session_missing_cert' });
      return false;
    }

    try {
      const res = await resilientFetch(
        `${base}/v1/business/sessions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ certificate, device_fingerprint: fingerprint }),
        },
        { timeoutMs: 10_000, retries: 1 },
      );

      if (!res.ok) {
        logger.warn({ event: 'installation_session_rejected', status: res.status });
        return false;
      }

      const body = (await res.json()) as BusinessSessionResponse;
      if (!body.session_token) return false;

      await SecureLicenceStore.setInstallationSessionToken(
        body.session_token,
        body.expires_at,
        body.tenant_id,
      );
      logger.info({ event: 'installation_session_opened' });
      return true;
    } catch {
      logger.warn({ event: 'installation_session_failed' });
      return false;
    }
  }
}
